// CLIENT PORTAL — the small shape under a hero figure.
//
// ⚠️⚠️ RESTORED 2026-09-02, AND THE DELETION WAS NOT A MISTAKE. This was cut the
// day before, when the Delivery band's five sparklines became five full chart
// cards: five silent gaps under five small marks were unremarkable, five cards
// with a figure and a blank space beneath read as broken. That reasoning is
// intact and the Delivery band keeps its charts.
//
// What is different here is the CONTEXT, which is the same distinction that
// reversed it the first time — same fact, different size, opposite answer. A
// hero band carries seven figures whose question is "which way has this been
// going", not "what was it in March"; seven full charts there would be a second
// report above the report. A mark beside a number is right at this size.
//
// ⚠️⚠️ IT SHOWS A SHAPE, NOT VALUES. There is no axis, no gridline, no label and
// no tooltip, and that is the whole design: the number above it is the figure,
// and this says whether it has been climbing, falling or flat. The moment it
// grows a readable scale it stops being a sparkline and starts being a chart
// that belongs in a card of its own.
//
// ⚠️⚠️ EVERY SPARKLINE SCALES INDEPENDENTLY, AND NOTHING IN THE LAYOUT MAY
// IMPLY OTHERWISE. Impressions run to millions and click-through rate to about
// one percent; a shared y-scale across the five would flatten four of them into
// a straight line at the bottom. That is also why they are never stacked into
// one aligned grid with a common baseline drawn across it.
//
// ⚠️ A NULL IS A BREAK, NEVER A ZERO AND NEVER A JOIN ACROSS. Each run of
// consecutive measured points is its own `<path>`; connecting across a gap would
// draw a confident line through a month we know nothing about, which is the
// graphical form of inventing data — the same rule `connectNulls={false}` keeps
// on the report's big charts.
//
// ⚠️ NO LEGEND. One series, named by the figure directly above it — a legend box
// would label a thing that is already labelled.
//
// ⚠️ A SINGLE MEASURED POINT BETWEEN GAPS IS DRAWN AS A DOT. A one-point path
// renders nothing at all, so without this a month of data would look identical
// to no data.
//
// ⚠️ It is `aria-hidden` with the description on the wrapper: a screen reader
// gets one sentence naming the metric and its range, rather than a bare graphic
// role it cannot interrogate. The FIGURE carries the value either way.

const W = 120;
const H = 26;
const PAD = 2; // room for the 2px stroke at the extremes

export interface SparklineProps {
  /** One value per bucket. `null` = unmeasured, or a ratio with no denominator. */
  values: ReadonlyArray<number | null>;
  /** Read out by assistive tech in place of the graphic. */
  label: string;
}

export function Sparkline({ values, label }: SparklineProps) {
  const measured = values.filter((v): v is number => v !== null);
  if (measured.length === 0) return null;

  const min = Math.min(...measured);
  const max = Math.max(...measured);
  // ⚠️ A FLAT SERIES IS DRAWN FLAT, THROUGH THE MIDDLE — not as a divide-by-zero
  // and not as a line pinned to the floor, which would read as "it fell to
  // nothing" when in fact it never moved.
  const span = max - min;
  const x = (i: number) =>
    values.length === 1 ? W / 2 : PAD + (i / (values.length - 1)) * (W - PAD * 2);
  const y = (v: number) =>
    span === 0 ? H / 2 : H - PAD - ((v - min) / span) * (H - PAD * 2);

  const segments: Array<Array<{ x: number; y: number }>> = [];
  let run: Array<{ x: number; y: number }> = [];
  values.forEach((v, i) => {
    if (v === null) {
      if (run.length > 0) segments.push(run);
      run = [];
      return;
    }
    run.push({ x: x(i), y: y(v) });
  });
  if (run.length > 0) segments.push(run);

  // ⚠️ Duplicate a lone point so its path is `M x y L x y` — a bare `M` draws
  // nothing at all, and a month of data would then look identical to no data.
  const drawn = segments.map((seg) => (seg.length === 1 ? [seg[0]!, seg[0]!] : seg));

  return (
    <span className="rpt-spark" role="img" aria-label={label}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        width="100%"
        height={H}
        aria-hidden="true"
        focusable="false"
      >
        {drawn.map((seg, i) => (
          <path
            key={i}
            // ⚠️ A ONE-POINT SEGMENT IS A ZERO-LENGTH SUBPATH (`M x y L x y`),
            // which SVG renders as a DOT under a round cap. Deliberately not a
            // `<circle>`: `preserveAspectRatio="none"` stretches the viewBox
            // horizontally, so a circle would come out an ellipse, whereas a
            // non-scaling stroke's cap stays round at any scale.
            d={seg.map((p, j) => `${j === 0 ? 'M' : 'L'}${p.x} ${p.y}`).join(' ')}
            fill="none"
            // ⚠️ NOT `--rpt-fig` (kelly). That is the report's numeral colour
            // and it belongs to the figures; a row of kelly lines would compete
            // with the numbers that matter. A muted neutral is right for a mark
            // carrying no identity of its own.
            //
            // ⚠️ THE TOKEN, on every surface. This was briefly a REQUIRED prop
            // because `--rpt-baseline` resolved to the LIGHT muted inside the
            // always-dark hero band; that was a defect in the stylesheet, now
            // fixed at the token (see the dark-band block in index.css), so the
            // prop was a workaround with no remaining reason to exist.
            stroke="var(--rpt-baseline)"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            // ⚠️ Keeps the stroke a true 2px, and the cap circular, under the
            // non-uniform scale.
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>
    </span>
  );
}
